import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  BottomSheet,
  Button,
  NumPad,
  useToast,
} from '../../../components';
import { useActions, useAppData } from '../../../data';
import { cn } from '../../../lib/cn';
import { formatCurrency } from '../../../lib/currency';

type Props = { open: boolean; onClose: () => void };

/**
 * Pay Yourself First Calculator. Edit hourly rate via the NumPad and
 * see daily / weekly / monthly / yearly savings projections (saving 1
 * hour of pay per day). The "Save to profile" button persists the rate
 * onto profile.hourlyRate, used by the PYF model on the Today and Plan
 * tabs.
 */
export function PYFCalculator({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const currency = data.profile.currency;

  const [rate, setRate] = useState(data.profile.hourlyRate ?? 0);

  useEffect(() => {
    if (open) setRate(data.profile.hourlyRate ?? 0);
  }, [open, data.profile.hourlyRate]);

  // Workdays/week from profile (3..7), defaults to 5. Drives every
  // projection so 4×10 / 3×12 schedules see accurate numbers.
  const wdpwRaw = data.profile.workDaysPerWeek;
  const workDaysPerWeek =
    typeof wdpwRaw === 'number'
      ? Math.max(1, Math.min(7, wdpwRaw))
      : 5;
  const monthlyHours = workDaysPerWeek * (52 / 12);
  const yearlyHours = workDaysPerWeek * 52;
  const daily = rate;
  const weekly = rate * workDaysPerWeek;
  const monthly = rate * monthlyHours;
  const yearly = rate * yearlyHours;

  const dirty = rate !== (data.profile.hourlyRate ?? 0);

  return (
    <BottomSheet open={open} onClose={onClose} title="Pay yourself first">
      <div className="px-card-pad pt-2 pb-3 flex flex-col gap-3">
        <p className="text-[14px] text-text-secondary text-center">
          Save 1 hour of pay every workday. Here’s what that builds.
        </p>

        <div className="rounded-card bg-surface-secondary p-card-pad flex flex-col gap-1.5">
          <ProjectionRow
            label="Per day (1 hr)"
            value={formatCurrency(daily, { currency })}
          />
          <ProjectionRow
            label={`Per week (${workDaysPerWeek} hrs)`}
            value={formatCurrency(weekly, { currency })}
          />
          <ProjectionRow
            label={`Per month (${Math.round(monthlyHours)} hrs)`}
            value={formatCurrency(monthly, { currency })}
          />
          <div className="h-px bg-text-tertiary/20 my-1" />
          <ProjectionRow
            label={`Per year (${yearlyHours} hrs)`}
            value={
              <span className="text-positive text-[18px] font-mono font-light tabular-nums">
                {formatCurrency(yearly, { currency })}
              </span>
            }
            highlighted
          />
        </div>

        <div
          className={cn(
            'rounded-card bg-positive/[0.08] p-card-pad flex items-start gap-3',
          )}
        >
          <div className="w-9 h-9 rounded-button bg-positive/15 inline-flex items-center justify-center text-positive shrink-0">
            <Sparkles size={18} strokeWidth={1.5} />
          </div>
          <p className="text-[13px] text-text-primary leading-snug">
            That’s{' '}
            <span className="font-semibold font-mono">
              {formatCurrency(yearly, { currency })}
            </span>{' '}
            a year by saving just 1 hour of pay each day.
          </p>
        </div>

        {dirty ? (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => {
              actions.updateProfile({ hourlyRate: rate });
              toast.show({ message: 'Hourly rate saved', kind: 'success' });
              onClose();
            }}
          >
            Save to profile
          </Button>
        ) : null}
      </div>

      <NumPad
        initialValue={rate}
        onChange={setRate}
        onDone={() => {
          if (rate > 0 && rate !== (data.profile.hourlyRate ?? 0)) {
            actions.updateProfile({ hourlyRate: rate });
            toast.show({ message: 'Hourly rate saved', kind: 'success' });
          }
          onClose();
        }}
        doneLabel={dirty ? 'Save & close' : 'Done'}
        caption="Hourly rate"
      />
    </BottomSheet>
  );
}

function ProjectionRow({
  label,
  value,
  highlighted,
}: {
  label: string;
  value: React.ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <span
        className={cn(
          'text-[13px]',
          highlighted ? 'text-text-primary font-medium' : 'text-text-secondary',
        )}
      >
        {label}
      </span>
      {typeof value === 'string' ? (
        <span className="text-[14px] font-mono tabular-nums text-text-primary">
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  );
}
